import type { InstagramAudioResult, InstagramPlaceResult } from '../../../shared/instagram-lookup';
import { BaseService } from '../base/BaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

export type { InstagramAudioResult, InstagramPlaceResult };

export type InstagramLookup<T> =
  | { state: 'ok'; results: T[] }
  | { state: 'unavailable'; message: string }
  | { state: 'not_connected' }
  | { state: 'error'; message: string };

class InstagramLookupService extends BaseService {
  private static instance: InstagramLookupService;

  private constructor() {
    super({ defaultTTL: 0, enableCache: false, enableRetry: false });
  }

  static getInstance(): InstagramLookupService {
    if (!InstagramLookupService.instance) {
      InstagramLookupService.instance = new InstagramLookupService();
    }
    return InstagramLookupService.instance;
  }

  private async invoke<T>(
    body: Record<string, unknown>,
  ): Promise<ServiceResponse<InstagramLookup<T>>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client.functions.invoke<InstagramLookup<T>>(
        'instagram-publish-lookup',
        { body },
      );
      if (error) throw error;
      if (!data || typeof data !== 'object' || !('state' in data)) {
        throw new Error('Instagram lookup returned an unexpected response');
      }
      return data;
    }, 'Failed to search Instagram');
  }

  searchAudio(query: string, audioType: 'music' | 'original_sound') {
    return this.invoke<InstagramAudioResult>({ kind: 'audio', query, audioType });
  }

  searchPlaces(query: string) {
    return this.invoke<InstagramPlaceResult>({ kind: 'location', query });
  }
}

export const instagramLookupService = InstagramLookupService.getInstance();
